import { Grid, Typography } from '@mui/material';
import { useState, useMemo } from 'react';
import { useQueryClient } from 'react-query';

// Our Components
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import SoraTextField from 'components/Input/SoraTextField';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Util Hooks
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// Our utils
import { clearFormValues, isSubmissionReady } from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our Constants
import { NUMBER } from 'components/Input/Types';
import {
	OTHER_OFFER_TYPE,
	OTHER_TRADELINE_TYPE
} from 'shared/constants/LoanInquiry/LoanInquiryTypes';

function OtherLoanForm({
	advisorInfo,
	clientInfo,
	clientProfileData,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const createDeal = useMutateCreateDeal();
	const sendGenericEmail = useMutateSendGenericEmail();
	const updateLoanRequest = useMutateUpdateLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Build full name
	const { clientFullName, clientId, firstName, lastName, creditScoreValue } =
		clientInfo;

	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Pre-populated state items
	const [loanAmount, setLoanAmount] = useState('');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	// Other fields
	const [additionalNotes, setAdditionalNotes] = useState('');

	// Modal
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	// Required Fields for Other Loan Form
	const loanRequestDetailsRequiredFields = [loanAmount];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(loanRequestDetailsRequiredFields) &&
			isClientNameProvided,
		loanRequestDetailsRequiredFields
	);

	usePrepopulateState(
		[
			{
				setState: setAddressLine1,
				value: `${clientProfileData?.address?.addressLine1}`
			},
			{
				setState: setAddressLine2,
				value: `${clientProfileData?.address?.addressLine2}`
			},
			{
				setState: setCity,
				value: `${clientProfileData?.address?.city}`
			},
			{
				setState,
				value: `${clientProfileData?.address?.state}`
			},
			{
				setState: setZipCode,
				value: `${clientProfileData?.address?.zipcode}`
			}
		],
		[clientProfileData]
	);

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScore: creditScoreValue,
			firstName,
			lastName,
			loanAmount,
			offerType: OTHER_OFFER_TYPE,
			tradelineType: OTHER_TRADELINE_TYPE,
			state,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];

		const subject = `Other Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`
			],
			additionalNotes,
			lendingTypeRequested: 'Other Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setLoanAmount,
						setState,
						setZipCode
					]);
				}
			}
		);
	};

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired loan amount:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				${formattedCollection}
				State: ${state}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest(loanRequestId);
		}
	};

	return (
		<>
			{savingLoanInquiry && <Loader />}

			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">Other Loan</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{!savingLoanInquiry && (
				<>
					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						<CurrencyTextInput
							label="Loan Amount *"
							value={loanAmount}
							onChange={setLoanAmount}
							type={NUMBER}
							inputProps={{
								'data-test': 'loanAmount'
							}}
						/>
					</Grid>
					<GetFullAddressPartialForm
						label={
							atLoanRequestDetailsUrl ? 'Address *' : 'Address'
						}
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
					/>
					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
					<Grid item xs={12}>
						<RequiredFieldsTooltip>
							<span>
								<PrimaryButton
									disabled={!isFormReady}
									onClick={submitForm}
								>
									Submit
								</PrimaryButton>
							</span>
						</RequiredFieldsTooltip>
					</Grid>
				</>
			)}
		</>
	);
}

export default OtherLoanForm;
